import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, CheckCircle, XCircle, Clock, Target, 
  ChevronRight, ChevronLeft, Trophy, RotateCcw,
  Sparkles, BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useSessionControl } from '../contexts/SessionControlContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_COLORS = {
  'easy': 'bg-green-100 text-green-700',
  'medium': 'bg-yellow-100 text-yellow-700',
  'hard': 'bg-red-100 text-red-700'
};

const CustomPracticePaper = () => {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showAnswers, setShowAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  
  const token = localStorage.getItem('token');
  const { startSession, logAnswer, endSession } = useSessionControl();
  const loggedQuestions = useRef(new Set());
  const lastInteractionTime = useRef(Date.now());

  useEffect(() => {
    loadPaper();
  }, [paperId]);

  const loadPaper = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai-practice/paper/${paperId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Paper not found');
      }
      
      const data = await response.json();
      setPaper(data);
      
      // Start SSCS session for fatigue monitoring
      const subject = data.topics?.[0] || 'practice';
      startSession('practice', subject);
      lastInteractionTime.current = Date.now();
    } catch (error) {
      console.error('Error loading paper:', error);
      toast.error('Failed to load paper');
      navigate('/ai-practice');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
    
    // Log first answer per question to SSCS for fatigue monitoring
    if (!loggedQuestions.current.has(questionId) && paper?.questions) {
      const question = paper.questions.find(q => q.id === questionId);
      if (question) {
        const responseTime = Math.round((Date.now() - lastInteractionTime.current) / 1000);
        const isCorrect = optionIndex === question.correct_answer;
        logAnswer(questionId, isCorrect, responseTime, question.difficulty || 'medium');
        loggedQuestions.current.add(questionId);
        lastInteractionTime.current = Date.now();
      }
    }
  };

  const toggleShowAnswer = (questionId) => {
    setShowAnswers(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const calculateScore = () => {
    if (!paper?.questions) return 0;
    
    let correct = 0;
    paper.questions.forEach(q => {
      if (answers[q.id] === q.correct_answer) {
        correct++;
      }
    });
    
    return Math.round((correct / paper.questions.length) * 100);
  };

  const handleComplete = async () => {
    const score = calculateScore();
    
    // End SSCS session
    endSession();
    
    try {
      await fetch(`${API_URL}/api/ai-practice/paper/${paperId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ score })
      });
      
      setShowResults(true);
      toast.success(`Paper completed! Score: ${score}%`);
    } catch (error) {
      console.error('Error saving result:', error);
      setShowResults(true);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setShowAnswers({});
    setShowResults(false);
    setCurrentQuestion(0);
    loggedQuestions.current = new Set();
    lastInteractionTime.current = Date.now();
    
    // Start a new SSCS session for the retake
    const subject = paper?.topics?.[0] || 'practice';
    startSession('practice', subject);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Paper not found</p>
      </div>
    );
  }

  const questions = paper.questions || [];
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length;
  const score = calculateScore();
  const correctCount = questions.filter(q => answers[q.id] === q.correct_answer).length;

  // Results View
  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto p-4 md:p-8">
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <div className="mx-auto w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Trophy className="h-10 w-10 text-purple-600" />
              </div>
              <CardTitle className="text-2xl">Practice Complete!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <div className="text-6xl font-bold text-purple-600 mb-2">{score}%</div>
                <p className="text-gray-600">
                  You got {correctCount} out of {questions.length} questions correct
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-700">{correctCount}</div>
                  <div className="text-sm text-green-600">Correct</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-red-700">{questions.length - correctCount}</div>
                  <div className="text-sm text-red-600">Incorrect</div>
                </div>
              </div>

              <div className="flex gap-4 justify-center pt-4">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/ai-practice')}
                  data-testid="back-to-generator"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Generator
                </Button>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={handleRetake}
                  data-testid="retake-paper"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retake Paper
                </Button>
              </div>

              {/* Review Questions */}
              <div className="mt-8 pt-8 border-t">
                <h3 className="font-semibold text-lg mb-4">Review Your Answers</h3>
                <div className="space-y-4">
                  {questions.map((q, idx) => {
                    const userAnswer = answers[q.id];
                    const isCorrect = userAnswer === q.correct_answer;
                    
                    return (
                      <Card 
                        key={q.id} 
                        className={`${isCorrect ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {isCorrect ? (
                              <CheckCircle className="h-5 w-5 text-green-600 mt-1 shrink-0" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600 mt-1 shrink-0" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium">Q{idx + 1}.</span>
                                <Badge className={DIFFICULTY_COLORS[q.difficulty]}>
                                  {q.difficulty}
                                </Badge>
                                <Badge variant="outline">{q.topic}</Badge>
                              </div>
                              <p className="text-gray-800 mb-3">{q.question}</p>
                              
                              <div className="space-y-2">
                                {q.options.map((opt, optIdx) => (
                                  <div 
                                    key={optIdx}
                                    className={`
                                      px-3 py-2 rounded-lg text-sm
                                      ${optIdx === q.correct_answer 
                                        ? 'bg-green-100 text-green-800 font-medium' 
                                        : optIdx === userAnswer && !isCorrect
                                          ? 'bg-red-100 text-red-800 line-through'
                                          : 'bg-gray-100 text-gray-600'
                                      }
                                    `}
                                  >
                                    {String.fromCharCode(65 + optIdx)}. {opt}
                                    {optIdx === q.correct_answer && ' ✓'}
                                  </div>
                                ))}
                              </div>

                              {q.explanation && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                  <p className="text-sm text-blue-800">
                                    <strong>Explanation:</strong> {q.explanation}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Practice View
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/ai-practice')}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  {paper.name}
                </h1>
                <p className="text-gray-500 text-sm">
                  {paper.topics?.length} topics • {questions.length} questions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-gray-500">Progress: </span>
                <span className="font-medium text-purple-600">
                  {answeredCount} / {questions.length}
                </span>
              </div>
              <Button 
                onClick={handleComplete}
                disabled={!allAnswered}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="complete-paper-btn"
              >
                Complete Paper
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="space-y-6">
          {questions.map((q, idx) => {
            const userAnswer = answers[q.id];
            const showAnswer = showAnswers[q.id];
            const isAnswered = userAnswer !== undefined;
            const isCorrect = userAnswer === q.correct_answer;

            return (
              <Card 
                key={q.id} 
                className={`shadow-lg transition-all ${
                  showAnswer 
                    ? isCorrect 
                      ? 'border-green-300 bg-green-50/30' 
                      : 'border-red-300 bg-red-50/30'
                    : ''
                }`}
                data-testid={`question-${q.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                      Q{idx + 1}
                    </span>
                    <Badge className={DIFFICULTY_COLORS[q.difficulty]}>
                      {q.difficulty}
                    </Badge>
                    <Badge variant="outline">{q.topic}</Badge>
                  </div>

                  <p className="text-lg text-gray-800 mb-6">{q.question}</p>

                  <div className="grid gap-3">
                    {q.options.map((option, optIdx) => {
                      const isSelected = userAnswer === optIdx;
                      const isCorrectOption = optIdx === q.correct_answer;
                      
                      let optionStyle = 'border-gray-200 hover:border-purple-300 hover:bg-purple-50';
                      if (showAnswer) {
                        if (isCorrectOption) {
                          optionStyle = 'border-green-500 bg-green-50';
                        } else if (isSelected && !isCorrectOption) {
                          optionStyle = 'border-red-500 bg-red-50';
                        }
                      } else if (isSelected) {
                        optionStyle = 'border-purple-500 bg-purple-50';
                      }

                      return (
                        <label
                          key={optIdx}
                          className={`
                            flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                            ${optionStyle}
                          `}
                          data-testid={`question-${q.id}-option-${optIdx}`}
                        >
                          <input
                            type="radio"
                            name={`question-${q.id}`}
                            checked={isSelected}
                            onChange={() => handleAnswer(q.id, optIdx)}
                            className="w-5 h-5 text-purple-600"
                            disabled={showAnswer}
                          />
                          <span className="flex-1">{option}</span>
                          {showAnswer && isCorrectOption && (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          )}
                          {showAnswer && isSelected && !isCorrectOption && (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {/* Show Answer Button */}
                  <div className="mt-4 flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleShowAnswer(q.id)}
                      disabled={!isAnswered}
                      data-testid={`show-answer-${q.id}`}
                    >
                      {showAnswer ? 'Hide Solution' : 'Show Solution'}
                    </Button>
                  </div>

                  {/* Explanation */}
                  {showAnswer && q.explanation && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <p className="text-sm font-medium text-blue-800 mb-2">Explanation:</p>
                      <p className="text-blue-700">{q.explanation}</p>
                      
                      {q.step_by_step && q.step_by_step.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-sm font-medium text-blue-800 mb-2">Step by Step:</p>
                          <ul className="space-y-1">
                            {q.step_by_step.map((step, stepIdx) => (
                              <li key={stepIdx} className="text-sm text-blue-700">
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Complete Button */}
        {allAnswered && (
          <div className="mt-8 text-center">
            <Button 
              size="lg"
              onClick={handleComplete}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="complete-paper-bottom"
            >
              <Trophy className="h-5 w-5 mr-2" />
              Complete Paper & See Results
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPracticePaper;
